import React, { useState, useRef } from "react";
import { Upload, Mic, Loader2, FileText, Sparkles, Copy, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

const LANGS = [
  { id: "", label: "Auto-detect" },
  { id: "en", label: "English" },
  { id: "es", label: "Spanish" },
  { id: "fr", label: "French" },
  { id: "de", label: "German" },
  { id: "pt", label: "Portuguese" },
  { id: "it", label: "Italian" },
  { id: "nl", label: "Dutch" },
  { id: "ar", label: "Arabic" },
  { id: "hi", label: "Hindi" },
  { id: "ja", label: "Japanese" },
  { id: "ko", label: "Korean" },
  { id: "zh", label: "Chinese" },
];

export default function Transcribe() {
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState("");
  const [autoNotes, setAutoNotes] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const onPick = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("File too large (25 MB max)"); return; }
    setFile(f);
  };

  const submit = async () => {
    if (!file) { toast.error("Choose an audio file first"); return; }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (language) fd.append("language", language);
      fd.append("auto_show_notes", autoNotes ? "true" : "false");
      const res = await fetch(`${API_BASE}/ai/transcribe`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) throw new Error("transcribe failed");
      const data = await res.json();
      setResult(data);
      toast.success("Transcription ready");
    } catch (e) {
      toast.error("Transcription failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-up" data-testid="transcribe-page">
      <PageHeader
        eyebrow="// TRANSCRIBE"
        title="From mic to show notes."
        description="Drop your recording. Whisper transcribes it, then Claude turns the raw text into polished show notes."
      />

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-6 space-y-5" data-testid="transcribe-form">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
            className="rounded-xl border-2 border-dashed border-line hover:border-brand-amber/60 transition cursor-pointer p-8 text-center"
            data-testid="dropzone"
          >
            <Upload className="h-8 w-8 mx-auto text-brand-amber mb-3" />
            <div className="type-section-heading text-ink-primary mb-1">
              {file ? file.name : "Drop your audio here"}
            </div>
            <div className="type-card-meta-label text-ink-muted font-mono">
              {file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : "MP3 · M4A · WAV · WEBM · up to 25 MB"}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".mp3,.m4a,.wav,.webm,.mp4,.mpeg,.mpga,audio/*"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="file-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Language</Label>
            <Select value={language || "auto"} onValueChange={(v) => setLanguage(v === "auto" ? "" : v)}>
              <SelectTrigger className="mt-1 bg-bg border-line" data-testid="lang-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-bg-elevated border-line">
                {LANGS.map((l) => <SelectItem key={l.id || "auto"} value={l.id || "auto"}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-line bg-bg-elevated p-3">
            <div>
              <div className="type-list-title text-ink-primary">Auto-generate show notes</div>
              <div className="type-list-subtitle text-ink-muted">Claude turns transcript into polished notes.</div>
            </div>
            <Switch checked={autoNotes} onCheckedChange={setAutoNotes} data-testid="auto-notes-toggle" />
          </div>

          <button
            onClick={submit}
            disabled={loading || !file}
            data-testid="transcribe-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-5 py-3 type-button-label text-black amber-glow disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
            {loading ? "Transcribing..." : "Transcribe audio"}
          </button>
        </div>

        <div className="lg:col-span-3 space-y-3" data-testid="transcribe-output">
          {loading && (
            <div className="rounded-xl border border-brand-amber/40 bg-bg-surface p-8 text-center">
              <div className="font-mono text-xs text-brand-amber animate-pulse">// WHISPER PROCESSING...</div>
              <div className="text-ink-secondary text-sm mt-2">Could take 10-60s depending on file length.</div>
            </div>
          )}
          {!loading && !result && (
            <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted text-sm">
              <FileText className="h-8 w-8 mx-auto mb-3 opacity-50" />
              Transcript and show notes will appear here.
            </div>
          )}
          {result && (
            <>
              <Block title="transcript" body={result.transcript} testid="result-transcript" />
              {result.show_notes && (
                <Block title="show notes (claude)" body={result.show_notes} testid="result-notes" accent />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Block({ title, body, testid, accent }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={`rounded-xl border bg-bg-surface p-5 ${accent ? "border-brand-amber/40" : "border-line"}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber flex items-center gap-2">
          {accent && <Sparkles className="h-3 w-3" />}
          {title}
        </div>
        <button
          onClick={() => { navigator.clipboard.writeText(body); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 text-xs hover:text-cyan-300 transition"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="font-sans text-sm whitespace-pre-wrap text-ink-primary leading-relaxed">{body}</pre>
    </div>
  );
}
